import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { TextField } from '@mui/material';

import { editorClasses } from '../classes';
import { ToolbarItem } from './toolbar-item';

import type { EditorToolbarProps } from '../types';

// ----------------------------------------------------------------------

const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF',
  '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF',
  '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
  '#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD',
  '#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0',
  '#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79',
  '#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47',
  '#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130',
];

export function ColorBlock({ editor }: Pick<EditorToolbarProps, 'editor'>) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [customColor, setCustomColor] = useState('#000000');

  const currentColor = editor?.getAttributes('textStyle')?.color || '#000000';

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setCustomColor(currentColor);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleSetColor = useCallback(
    (color: string) => {
      if (editor) {
        editor.chain().focus().setMark('textStyle', { color }).run();
        handleClosePopover();
      }
    },
    [editor]
  );

  const handleSetCustomColor = useCallback(() => {
    if (customColor && editor) {
      editor.chain().focus().setMark('textStyle', { color: customColor }).run();
      handleClosePopover();
    }
  }, [customColor, editor]);

  const handleRemoveColor = useCallback(() => {
    if (editor) {
      editor.chain().focus().unsetMark('textStyle').run();
      handleClosePopover();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <ToolbarItem
        aria-label="Text color"
        active={!!editor.getAttributes('textStyle')?.color}
        className={editorClasses.toolbar.color}
        onClick={handleOpenPopover}
        icon={
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2Z"
              fill={currentColor}
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M6 14L14 6M6 6L14 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        }
      />

      <Popover
        id={anchorEl ? 'color-popover' : undefined}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2.5, width: 280 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Text Color
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(10, 1fr)',
            gap: 0.5,
            mb: 2,
          }}
        >
          {COLORS.map((color) => (
            <Box
              key={color}
              onClick={() => handleSetColor(color)}
              sx={{
                width: 24,
                height: 24,
                backgroundColor: color,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              }}
            />
          ))}
        </Box>

        <Box gap={1} display="flex" alignItems="center" sx={{ mb: 1 }}>
          <input
            type="color"
            value={customColor}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCustomColor(event.target.value);
            }}
            style={{
              width: 80,
              height: 40,
              cursor: 'pointer',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          />
          <Button variant="contained" size="small" onClick={handleSetCustomColor}>
            Apply
          </Button>
        </Box>

        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={handleRemoveColor}
          sx={{ mt: 1 }}
        >
          Remove Color
        </Button>
      </Popover>
    </>
  );
}

